"use client";

import { fetchCommentsByPostId } from "@/sanity/lib/comments/fetchCommentsById";
import { submitComment } from "@/sanity/lib/comments/submitComment";
import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";

type Comment = {
  _id: string;
  authorName: string;
  commentText: string;
  timestamp: string;
};

export function Comments({ postId }: { postId: string }) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState({
    authorName: "",
    commentText: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loadingComments, setLoadingComments] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [visibleComments, setVisibleComments] = useState(5);

  // Fetch comments for the post
  useEffect(() => {
    const fetchComments = async () => {
      try {
        const fetchedComments = await fetchCommentsByPostId(postId);
        setComments(fetchedComments);
      } catch (error) {
        console.error("Error fetching comments:", error);
        setFetchError(true);
      } finally {
        setLoadingComments(false);
      }
    };

    fetchComments();
  }, [postId]);

  // Handle comment submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.authorName || !newComment.commentText) {
      setError("Name and Comment are required.");
      return;
    }
    setError("");
    setLoading(true);

    try {
      const sanitizedComment = DOMPurify.sanitize(newComment.commentText);

      const addedComment = await submitComment({
        postId,
        authorName: newComment.authorName,
        commentText: sanitizedComment,
      });

      setComments((prev) => [...prev, addedComment]);
      setNewComment({ authorName: "", commentText: "" });
    } catch (error) {
      console.error("Error adding comment:", error);
      setError("Failed to post comment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle "Load More" comments
  const handleLoadMore = () => {
    setVisibleComments((prev) => prev + 5);
  };

  return (
    <div className="mt-8 max-w-4xl mx-auto">
      <h3 className="text-xl font-bold mb-4">Comments</h3>

      {/* Comment Form */}
      <form onSubmit={handleSubmit} className="mb-6">
        {error && <p className="text-red-600 mb-2">{error}</p>}
        <input
          type="text"
          placeholder="Your Name"
          value={newComment.authorName}
          onChange={(e) =>
            setNewComment({ ...newComment, authorName: e.target.value })
          }
          className="w-full p-2 mb-2 border border-gray-300 rounded"
        />
        <textarea
          placeholder="Your Comment"
          value={newComment.commentText}
          onChange={(e) =>
            setNewComment({ ...newComment, commentText: e.target.value })
          }
          className="w-full p-2 mb-2 border border-gray-300 rounded"
          rows={4}
        />
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded"
          disabled={loading}
        >
          {loading ? "Posting..." : "Post Comment"}
        </button>
      </form>

      {/* Loading State for Comments */}
      {loadingComments ? (
        <p>Loading comments...</p>
      ) : fetchError ? (
        <p className="text-red-600">
          Failed to load comments. Please try again.
        </p>
      ) : (
        <ul className="divide-y divide-gray-300">
          {comments.slice(0, visibleComments).map((comment) => (
            <li key={comment._id} className="py-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-800">
                  {comment.authorName}
                </p>
                <p className="text-xs text-gray-500">
                  {new Date(comment.timestamp).toLocaleString()}
                </p>
              </div>
              <p className="text-gray-700 mt-2">{comment.commentText}</p>
            </li>
          ))}
        </ul>
      )}

      {comments.length > visibleComments && (
        <button
          onClick={handleLoadMore}
          className="mt-4 px-4 py-2 bg-gray-300 text-gray-800 rounded"
        >
          Load More Comments
        </button>
      )}
    </div>
  );
}
